import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, ChevronRight, X } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { readRequestDraft, saveRequestDraft } from "./requestDraftSession";
import "./RequestFlow.scss";
import WizardSteps from "./WizardSteps";

const pickId = (...vals) => vals.find((v) => v !== null && v !== undefined && v !== "") || "";

const SUBCATEGORIES_PAGE_SIZE = 500;

/** Same breakpoint as PatientAppLayout sidebar — sheet picker for procedure depth on small screens. */
const PROCEDURE_SHEET_MAX_WIDTH_PX = 1023;

function useMatchMaxWidth(maxPx) {
  const q = `(max-width: ${maxPx}px)`;
  const [matches, setMatches] = useState(
    () => (typeof window !== "undefined" ? window.matchMedia(q).matches : false)
  );
  useEffect(() => {
    const mq = window.matchMedia(q);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [q]);
  return matches;
}

/** Public list is paginated; load every page so parent/child links are complete (Continue vs server leaf check). */
async function fetchAllSubcategoriesForCategory(categoryId) {
  const cid = String(categoryId || "").trim();
  if (!cid) return [];
  const merged = [];
  let page = 1;
  const maxPages = 50;
  while (page <= maxPages) {
    const h = await apiRequest({
      method: "GET",
      url: ENDPOINTS.GET_SUBCATEGORIES,
      params: { categoryId: cid, limit: SUBCATEGORIES_PAGE_SIZE, page },
    });
    const batch = Array.isArray(h?.items) ? h.items : [];
    merged.push(...batch);
    if (batch.length < SUBCATEGORIES_PAGE_SIZE) break;
    page += 1;
  }
  return merged;
}

export default function RequestCategory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromCarePrice = Boolean(location?.state?.fromCarePrice);
  const isMobileProcedureUi = useMatchMaxWidth(PROCEDURE_SHEET_MAX_WIDTH_PX);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [err, setErr] = useState("");
  const [categories, setCategories] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [procedureSheetOpen, setProcedureSheetOpen] = useState(false);

  /** Categories + restore draft selection (category id must match hierarchy we load next). */
  useEffect(() => {
    let cancel = false;
    (async () => {
      setCategoriesLoading(true);
      setErr("");
      try {
        const res = await apiRequest({ method: "GET", url: ENDPOINTS.GET_CATEGORIES, params: { limit: 100 } });
        const rows = Array.isArray(res?.items) ? res.items : [];
        if (!cancel) setCategories(rows);

        const draft = readRequestDraft(id);
        let nextIds = Array.isArray(draft.selectedIds) ? [...draft.selectedIds] : [];
        let nextNames = Array.isArray(draft.selectedNames) ? [...draft.selectedNames] : [];
        // Desktop: pre-select first category. Mobile: leave empty so level 1 is chosen on the page first; deeper levels use the sheet after the user taps “Open procedure selector”.
        const isMobile =
          typeof window !== "undefined" &&
          window.matchMedia(`(max-width: ${PROCEDURE_SHEET_MAX_WIDTH_PX}px)`).matches;
        if (!nextIds.length && rows.length && !isMobile) {
          const catId = pickId(rows[0]?._id, rows[0]?.id, rows[0]?.categoryId);
          nextIds = [catId];
          nextNames = [rows[0]?.name || ""];
          if (!cancel) saveRequestDraft(id, { selectedIds: nextIds, selectedNames: nextNames });
        }
        if (!cancel) {
          setSelectedIds(nextIds);
          setSelectedNames(nextNames);
        }
      } catch (e) {
        if (!cancel) setErr(e?.message || "Could not load categories.");
      } finally {
        if (!cancel) setCategoriesLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id]);

  const selectedCategoryId = selectedIds[0] || "";

  /** All subcategories for the active top-level category (paginated API → fetch every page). */
  /** useLayoutEffect: set loading synchronously before paint so mobile sheet logic does not briefly see hierarchyLoading=false and close the picker. */
  useLayoutEffect(() => {
    const catId = String(selectedCategoryId || "").trim();
    if (!catId) {
      setHierarchy([]);
      setHierarchyLoading(false);
      return undefined;
    }
    setHierarchyLoading(true);
    let cancel = false;
    setErr("");
    (async () => {
      try {
        const allSubs = await fetchAllSubcategoriesForCategory(catId);
        if (!cancel) setHierarchy(allSubs);
      } catch (e) {
        if (!cancel) setErr(e?.message || "Could not load procedures.");
      } finally {
        if (!cancel) setHierarchyLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [selectedCategoryId]);

  const rowsByParent = useMemo(() => {
    const map = new Map();
    for (const row of hierarchy) {
      const pid = row?.parentSubCategoryId == null ? "__root__" : String(row.parentSubCategoryId);
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(row);
    }
    return map;
  }, [hierarchy]);

  const levels = useMemo(() => {
    const out = [];
    const level1 = categories.map((c) => ({
      id: pickId(c?._id, c?.id, c?.categoryId),
      name: c?.name || "Category",
      isCategory: true,
    }));
    out.push(level1);
    let parent = null;
    for (let idx = 1; idx < 5; idx += 1) {
      const key = parent == null ? "__root__" : String(parent);
      const next = (rowsByParent.get(key) || []).filter((r) => {
        const cId = pickId(r?.categoryId, r?.category?._id);
        return !cId || String(cId) === String(selectedCategoryId);
      });
      if (!next.length) break;
      out.push(
        next.map((r) => ({
          id: pickId(r?.subCategoryId, r?._id, r?.id),
          name: r?.subCategoryName || r?.name || "Procedure",
          isCategory: false,
        }))
      );
      parent = selectedIds[idx] || null;
      if (!parent) break;
    }
    return out;
  }, [categories, rowsByParent, selectedIds, selectedCategoryId]);

  const hasChildren = (subId) =>
    hierarchy.some((h) => {
      if (String(h?.parentSubCategoryId) !== String(subId)) return false;
      const cId = pickId(h?.categoryId, h?.category?._id);
      return !cId || String(cId) === String(selectedCategoryId);
    });
  const finalSubId = selectedIds[selectedIds.length - 1] || "";
  const canContinue = finalSubId && selectedIds.length > 1 && !hasChildren(finalSubId);

  const sheetLevelIdx = selectedIds.length;
  const sheetRows = levels[sheetLevelIdx];
  const sheetNeedsHierarchy = sheetLevelIdx >= 1;
  const needsProcedureSheet = Boolean(
    !categoriesLoading &&
    !err &&
    (!sheetNeedsHierarchy || !hierarchyLoading) &&
    Array.isArray(sheetRows) &&
    sheetRows.length > 0 &&
    selectedIds.length < levels.length
  );

  /** Fetched hierarchy for chosen category — only level 1 exists (no root subcategories). */
  const noSubProceduresForCategory =
    !hierarchyLoading &&
    !!String(selectedCategoryId || "").trim() &&
    selectedIds.length >= 1 &&
    levels.length === 1;

  const mobileProcedureSheetLoading =
    isMobileProcedureUi &&
    procedureSheetOpen &&
    sheetLevelIdx >= 1 &&
    !!selectedCategoryId &&
    hierarchyLoading;

  const mobileProcedureSheetEmpty =
    isMobileProcedureUi && procedureSheetOpen && sheetLevelIdx >= 1 && noSubProceduresForCategory;

  const showMobileProcedureSheetPortal =
    isMobileProcedureUi &&
    procedureSheetOpen &&
    sheetLevelIdx >= 1 &&
    (mobileProcedureSheetLoading || mobileProcedureSheetEmpty || Boolean(sheetRows?.length));

  useEffect(() => {
    if (!isMobileProcedureUi) {
      setProcedureSheetOpen(false);
      return;
    }
    if (!procedureSheetOpen) return;
    if (mobileProcedureSheetLoading) return;
    if (mobileProcedureSheetEmpty) return;
    if (!needsProcedureSheet && !hierarchyLoading) {
      setProcedureSheetOpen(false);
    }
  }, [
    isMobileProcedureUi,
    needsProcedureSheet,
    hierarchyLoading,
    procedureSheetOpen,
    mobileProcedureSheetLoading,
    mobileProcedureSheetEmpty,
  ]);

  useEffect(() => {
    if (!procedureSheetOpen || !isMobileProcedureUi) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [procedureSheetOpen, isMobileProcedureUi]);

  const dismissProcedureSheet = useCallback(() => {
    setProcedureSheetOpen(false);
  }, []);

  const goBackFromProcedure = useCallback(() => {
    if (fromCarePrice && id && /^[a-f0-9]{24}$/i.test(String(id))) {
      navigate("/care-price", { state: { matchRequestId: String(id) } });
      return;
    }
    navigate(`/request/${id}/upload`);
  }, [fromCarePrice, id, navigate]);

  useEffect(() => {
    if (!procedureSheetOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismissProcedureSheet();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [procedureSheetOpen, dismissProcedureSheet]);

  const sheetModalBack = useCallback(() => {
    if (selectedIds.length <= 1) {
      dismissProcedureSheet();
      return;
    }
    const nextIds = selectedIds.slice(0, -1);
    const nextNames = selectedNames.slice(0, -1);
    setSelectedIds(nextIds);
    setSelectedNames(nextNames);
    saveRequestDraft(id, { selectedIds: nextIds, selectedNames: nextNames });
  }, [selectedIds, selectedNames, id, dismissProcedureSheet]);

  const onPick = (levelIdx, row) => {
    const nextIds = [...selectedIds.slice(0, levelIdx), row.id];
    const nextNames = [...selectedNames.slice(0, levelIdx), row.name];
    setSelectedIds(nextIds);
    setSelectedNames(nextNames);
    saveRequestDraft(id, { selectedIds: nextIds, selectedNames: nextNames });
  };

  const reopenProcedureSheet = useCallback(() => setProcedureSheetOpen(true), []);

  return (
    <section className="reqWizard">
      <div className="reqWizard__card">
        <button type="button" className="reqWizard__back reqWizard__back--dupGlobalNav" onClick={goBackFromProcedure}>
          <ArrowLeft size={16} />
          Back
        </button>
        <p className="reqWizard__eyebrow">New care request</p>
        <h1 className="reqWizard__title">Tell us what you need</h1>
        <p className="reqWizard__subtitle">Upload documents and pick the exact procedure. Doctors will bid once verified.</p>

        <WizardSteps active={1} />

        <h2 className="reqWizard__sectionTitle">Pick the exact procedure</h2>
        <p className="reqWizard__sectionSub">
          Narrow the list up to five steps when needed — stop as soon as you reach the option that fits your case (e.g. level 2 or 3 is fine if that is the right match).
        </p>
        {selectedNames.length ? (
          <div className="reqWizard__crumbs">
            {selectedNames.map((name, i) => <span key={`${name}-${i}`}>{name}</span>)}
          </div>
        ) : null}

        {categoriesLoading ? (
          <div className="reqWizard__procSkeleton" aria-busy="true" aria-label="Loading specialties">
            <p className="reqWizard__procSkeletonLead">Finding specialties…</p>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="reqWizard__procSkeletonRow" />
            ))}
          </div>
        ) : null}
        {err ? <p className="reqWizard__error">{err}</p> : null}
        {!categoriesLoading && !err ? (
          <>
            <div className={`reqWizard__catGrid${isMobileProcedureUi ? " reqWizard__catGrid--mobileL1" : ""}`}>
              {isMobileProcedureUi && !(procedureSheetOpen && sheetLevelIdx === 0) ? (
                <section className="reqWizard__catLevel" aria-labelledby="req-cat-l1-heading">
                  <p id="req-cat-l1-heading" className="reqWizard__catLevelTitle">
                    Level 1
                  </p>
                  <p className="reqWizard__mobileL1Hint">
                    Pick a category below — a panel opens to choose deeper procedure steps when needed.
                  </p>
                  <div className="reqWizard__catList">
                    {levels[0].map((row) => {
                      const active = selectedIds[0] === row.id;
                      return (
                        <button
                          key={row.id}
                          type="button"
                          className={`reqWizard__catItem${active ? " is-active" : ""}`}
                          onClick={() => {
                            if (isMobileProcedureUi) setProcedureSheetOpen(true);
                            onPick(0, row);
                          }}
                        >
                          <span>{row.name}</span>
                          <ChevronRight size={14} />
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              {!isMobileProcedureUi ? (
                levels.map((levelRows, idx) => (
                  <section key={`lv-${idx}`} className="reqWizard__catLevel">
                    <p className="reqWizard__catLevelTitle">Level {idx + 1}</p>
                    <div className="reqWizard__catList">
                      {levelRows.map((row) => {
                        const active = selectedIds[idx] === row.id;
                        return (
                          <button
                            key={row.id}
                            type="button"
                            className={`reqWizard__catItem${active ? " is-active" : ""}`}
                            onClick={() => onPick(idx, row)}
                          >
                            <span>{row.name}</span>
                            <ChevronRight size={14} />
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))
              ) : null}
            </div>
            {isMobileProcedureUi && needsProcedureSheet && !procedureSheetOpen && sheetLevelIdx >= 1 ? (
              <div className="reqWizard__sheetPrompt">
                <p className="reqWizard__sheetPromptText">
                  Refine your procedure (step {Math.min(sheetLevelIdx + 1, 5)} of up to 5). Tap the button to open the selector.
                </p>
                <button type="button" className="reqWizard__sheetPromptBtn" onClick={reopenProcedureSheet}>
                  Open procedure selector
                </button>
              </div>
            ) : null}
            {showMobileProcedureSheetPortal ? (
              <ProcedurePickerSheetPortal
                levelNumber={sheetLevelIdx + 1}
                breadcrumb={selectedNames.filter(Boolean)}
                rows={sheetRows || []}
                selectedId={selectedIds[sheetLevelIdx]}
                onPickRow={(row) => onPick(sheetLevelIdx, row)}
                onBack={sheetModalBack}
                onDismiss={dismissProcedureSheet}
                loading={mobileProcedureSheetLoading}
                emptyNoSubs={mobileProcedureSheetEmpty}
              />
            ) : null}
          </>
        ) : null}

        <div className="reqWizard__actions">
          <button type="button" className="reqWizard__ghost reqWizard__ghost--dupWithCardBack" onClick={goBackFromProcedure}>
            Back
          </button>
          <button type="button" className="reqWizard__primary" disabled={!canContinue} onClick={() => navigate(`/request/${id}/budget`)}>
            Continue
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}

function ProcedurePickerSheetPortal({
  levelNumber,
  breadcrumb,
  rows,
  selectedId,
  onPickRow,
  onBack,
  onDismiss,
  loading = false,
  emptyNoSubs = false,
}) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="reqWizard__sheetRoot" role="presentation">
      <button type="button" className="reqWizard__sheetBackdrop" aria-label="Close" onClick={onDismiss} />
      <div
        className="reqWizard__sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="req-procedure-sheet-title"
      >
        <div className="reqWizard__sheetChrome">
          <span className="reqWizard__sheetGrab" aria-hidden />
        </div>
        <header className="reqWizard__sheetHeader">
          <button type="button" className="reqWizard__sheetIconBtn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={22} strokeWidth={2} />
          </button>
          <div className="reqWizard__sheetHeaderMid">
            <p className="reqWizard__sheetStepLabel">
              Step {levelNumber} of 5 · refine procedure
            </p>
            <h2 id="req-procedure-sheet-title" className="reqWizard__sheetTitle">
              {loading ? "Loading" : emptyNoSubs ? "No finer options" : "Choose an option"}
            </h2>
            {breadcrumb.length ? (
              <div className="reqWizard__sheetTrail" aria-label="Selection path">
                {breadcrumb.map((name, i) => (
                  <span key={`${name}-${i}`} className="reqWizard__sheetTrailChip">
                    {name}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <button type="button" className="reqWizard__sheetIconBtn reqWizard__sheetIconBtn--close" onClick={onDismiss} aria-label="Close">
            <X size={22} strokeWidth={2} />
          </button>
        </header>
        <div className="reqWizard__sheetBody">
          {loading ? (
            <div className="reqWizard__procSkeleton reqWizard__procSkeleton--sheet" aria-busy="true" role="status">
              <p className="reqWizard__procSkeletonLead">Finding matching procedures…</p>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="reqWizard__procSkeletonRow" />
              ))}
            </div>
          ) : emptyNoSubs ? (
            <p className="reqWizard__sheetListLead">
              No sub-procedures are listed for this specialty yet. Go back to pick a different category, or close and try again later.
            </p>
          ) : (
            <>
              <p className="reqWizard__sheetListLead">Tap the row that matches your case — you can stop when it feels specific enough.</p>
              <ul className="reqWizard__sheetList">
                {rows.map((row) => {
                  const active = String(selectedId || "") === String(row.id || "");
                  return (
                    <li key={row.id}>
                      <button
                        type="button"
                        className={`reqWizard__sheetRow${active ? " is-active" : ""}`}
                        onClick={() => onPickRow(row)}
                      >
                        <span className="reqWizard__sheetRowLabel">{row.name}</span>
                        <span className="reqWizard__sheetRowChev" aria-hidden>
                          <ChevronRight size={18} strokeWidth={2} />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
